/**
 * First-party usage telemetry, sent to our own API (/api/telemetry). No
 * third-party script and no other domain.
 *
 * trackEvent() is fire-and-forget: it returns immediately, never throws, and a
 * failed send is logged and swallowed, so telemetry can never break a page.
 * Only an event name from a fixed list, the current path, and (for some
 * events) one detail word from that event's fixed list or a whole number of
 * seconds are sent; the API attaches the user and organization from the auth
 * token. A detail outside the list is never sent, so no typed text, search
 * words or error messages leave the browser.
 *
 * AuthProvider registers its authFetch as the sender; until it does, events
 * are dropped.
 */

export const TELEMETRY_ENDPOINT = '/api/telemetry';

export const TELEMETRY_EVENTS = Object.freeze([
  'page_view',
  'session_start',
  'call_started',
  'call_finished',
  'results_viewed',
  'feedback_submitted',
  'page_left',
  'call_start_pressed',
  'call_connect_failed',
  'call_dropped',
  'score_shown',
  'scoring_failed',
  'scoring_retried',
  'results_next_step',
  'setup_step_reached',
  'setup_step_skipped',
  'setup_finished',
  'company_basics_submitted',
  'invitation_sent',
  'search_used',
  'filter_chosen',
  'tour_finished',
  'tour_skipped'
]);

const SETUP_STEPS = Object.freeze([
  'industry', 'company', 'service_lines', 'packages', 'objections', 'competitors',
  'ai_customer', 'ai_coaching', 'ai_scoring', 'team', 'review'
]);

// The detail words each event may carry; the API and the database enforce the
// same lists.
export const TELEMETRY_DETAILS = Object.freeze({
  call_connect_failed: Object.freeze(['server_setup', 'voice_connect']),
  call_dropped: Object.freeze(['before_connect', 'during_call']),
  scoring_failed: Object.freeze(['too_short', 'timed_out', 'network', 'server']),
  results_next_step: Object.freeze(['same_scenario', 'new_scenario', 'back_to_module', 'open_replay']),
  setup_step_reached: SETUP_STEPS,
  setup_step_skipped: SETUP_STEPS,
  filter_chosen: Object.freeze([
    'difficulty_all', 'difficulty_easy', 'difficulty_medium', 'difficulty_hard',
    'date_all', 'date_today', 'date_week', 'date_month',
    'period_weekly', 'period_monthly', 'period_alltime'
  ])
});

let sender = null;

export function setTelemetrySender(fn) {
  sender = typeof fn === 'function' ? fn : null;
}

function currentPath() {
  try {
    return typeof window !== 'undefined' ? window.location?.pathname : undefined;
  } catch {
    return undefined;
  }
}

/**
 * trackEvent(event) / trackEvent(event, page) / trackEvent(event, { page, detail, seconds }).
 * An event whose detail is not on its list is dropped, not sent.
 */
export function trackEvent(event, pageOrOptions) {
  try {
    if (!sender || !TELEMETRY_EVENTS.includes(event)) return;
    const options = pageOrOptions && typeof pageOrOptions === 'object' ? pageOrOptions : { page: pageOrOptions };
    const allowed = Object.prototype.hasOwnProperty.call(TELEMETRY_DETAILS, event) ? TELEMETRY_DETAILS[event] : null;
    if (allowed && !allowed.includes(options.detail)) return;
    const path = typeof options.page === 'string' ? options.page.split(/[?#]/)[0] : currentPath();
    const body = { event, page: path };
    if (allowed) body.detail = options.detail;
    if (typeof options.seconds === 'number' && Number.isFinite(options.seconds)) body.seconds = Math.round(options.seconds);
    const pending = sender(TELEMETRY_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify(body)
    });
    if (pending && typeof pending.catch === 'function') {
      pending.catch((err) => console.warn('[Telemetry] Send failed:', event, err?.message || err));
    }
  } catch (err) {
    console.warn('[Telemetry] Send failed:', event, err?.message || err);
  }
}
